import { Controller } from "@hotwired/stimulus";
import { Turbo } from "@hotwired/turbo-rails";
import Swal from "sweetalert2/dist/sweetalert2";

export default class extends Controller {
  static values = {
    package: String,
    licenses: Number,
    currency: { type: String, default: "EUR" },
  };

  show(event) {
    event.preventDefault();
    const form = this.element.closest("form");

    // Read form field values
    const amount =
      form.querySelector("#group_invoice_form_invoice_price_gross")?.value ||
      "0";
    const discount =
      form.querySelector("#group_invoice_form_invoice_discount_gross")?.value ||
      "0";
    const recipient =
      form.querySelector("#group_invoice_form_invoice_email")?.value || "";
    const recipientCc =
      form.querySelector("#group_invoice_form_invoice_email_cc")?.value || "";
    const invoiceType =
      form.querySelector("#group_invoice_form_invoice_type")?.value || "";
    const invoiceXmlEmail =
      form.querySelector("#group_invoice_form_invoice_xml_email")?.value || "";
    const invoiceBuyerReference =
      form.querySelector("#group_invoice_form_invoice_buyer_reference")
        ?.value || "";
    const orderStartsAt =
      form.querySelector("#group_invoice_form_order_starts_at")?.value || "";
    const orderEndsAt =
      form.querySelector("#group_invoice_form_order_ends_at")?.value || "";

    // Build license duration display
    const laufzeitDisplay = this.buildLaufzeitLine(orderStartsAt, orderEndsAt);

    // Build workshop items display
    const workshopItems = this.getSelectedWorkshopItems(form);
    const workshopItemsSection = this.buildWorkshopItemsSection(workshopItems);

    // Determine invoice format display text
    const isEInvoice = invoiceType === "pdf_xml";
    const invoiceFormatDisplay = isEInvoice
      ? "eRechnung (ZUGPFeRD 2.2)"
      : "Standardrechnung (pdf)";

    // Build eRechnung recipient line if applicable
    const eInvoiceRecipientLine = isEInvoice
      ? `<p><strong>eRechnungs-Empfänger:</strong> ${this.escapeHtml(invoiceXmlEmail)}</p>`
      : "";

    // Build warning when eRechnung is selected but no Leitweg-ID is present
    const showBuyerReferenceWarning = isEInvoice && !invoiceBuyerReference;
    const missingBuyerReferenceWarning = showBuyerReferenceWarning
      ? `<p style="background-color: #fff3cd; border: 1px solid #ffc107; border-radius: 4px; padding: 8px 12px; margin-top: 8px; color: #664d03;">&#9888;&#65039; Keine Leitweg-ID vorhanden. Die eRechnung wird ohne Leitweg-ID erstellt. Bitte pr\u00fcfen, ob das korrekt ist.</p>`
      : "";

    // Build CC recipient line if applicable
    const ccRecipientLine = recipientCc
      ? `<p><strong>CC-Empfänger:</strong> ${this.escapeHtml(recipientCc)}</p>`
      : "";

    const htmlContent = `
      <div style="text-align: left;">
        <p><strong>Paket:</strong> ${this.escapeHtml(this.packageValue)}</p>
        <p><strong>Anzahl Lizenzen / Größe Paket:</strong> ${this.licensesValue}</p>
        <p><strong>Laufzeit:</strong> ${this.escapeHtml(laufzeitDisplay)}</p>
        <p><strong>Betrag:</strong> ${this.escapeHtml(this.formatPrice(amount))} ${this.escapeHtml(this.currencyValue)}</p>
        <p><strong>Rabatt:</strong> ${this.escapeHtml(this.formatPrice(discount))} ${this.escapeHtml(this.currencyValue)}</p>
        ${workshopItemsSection}
        <p><strong>Empfänger:</strong> ${this.escapeHtml(recipient)}</p>
        ${ccRecipientLine}
        <p><strong>Rechnungsformat:</strong> ${this.escapeHtml(invoiceFormatDisplay)}</p>
        ${eInvoiceRecipientLine}
        ${missingBuyerReferenceWarning}
      </div>
    `;

    Swal.fire({
      title: "Rechnung bestätigen",
      html: htmlContent,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Passt, jetzt erzeugen und verschicken",
      cancelButtonText: "Zurück zur Bearbeitung",
      reverseButtons: true,
    }).then((result) => {
      if (result.isConfirmed) {
        Turbo.navigator.submitForm(form);
      }
    });
  }

  escapeHtml(text) {
    const div = document.createElement("div");
    div.textContent = text;
    return div.innerHTML;
  }

  formatDate(dateString) {
    if (!dateString) return null;
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return null;
    return date.toLocaleDateString("de-DE", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  }

  buildLaufzeitLine(startsAt, endsAt) {
    const formattedStart = this.formatDate(startsAt);
    const formattedEnd = this.formatDate(endsAt);

    if (formattedStart && formattedEnd) {
      return `${formattedStart} - ${formattedEnd}`;
    } else if (formattedStart) {
      return `ab ${formattedStart}`;
    } else if (formattedEnd) {
      return `bis ${formattedEnd}`;
    }
    return "nicht angegeben";
  }

  getSelectedWorkshopItems(form) {
    const items = [];

    // Find the Workshops legend and get checkboxes in that section
    const legends = form.querySelectorAll("legend");
    let workshopLegend = null;
    legends.forEach((legend) => {
      if (legend.textContent.trim() === "Workshops") {
        workshopLegend = legend;
      }
    });

    if (workshopLegend) {
      // Get the container that follows the legend (three-columns div)
      const workshopContainer =
        workshopLegend.nextElementSibling?.nextElementSibling;
      if (workshopContainer) {
        const checkboxes = workshopContainer.querySelectorAll(
          'input[type="checkbox"][data-type="offerable_item"]:checked'
        );
        checkboxes.forEach((checkbox) => {
          const label = form.querySelector(`label[for="${checkbox.id}"]`);
          if (label) {
            // Extract title from label (remove price in parentheses)
            const labelText = label.textContent.trim();
            const title = labelText
              .replace(/\s*\(\d+(?:[,.]?\d*)?\s*€?\)\s*$/, "")
              .trim();

            // Get the actual price from the price input field
            const priceInputId = checkbox.id.replace(
              "_offerable_item_id",
              "_price"
            );
            const priceInput = form.querySelector(`#${priceInputId}`);
            const price = priceInput?.value || "0";

            items.push({ title, price });
          }
        });
      }
    }

    // Check individual workshop item (text field, not checkbox)
    const individualTitle = form
      .querySelector(
        "#group_invoice_form_invoice_items_tmp_individual_workshop_item_title"
      )
      ?.value?.trim();
    const individualPrice =
      form.querySelector(
        "#group_invoice_form_invoice_items_tmp_individual_workshop_item_price"
      )?.value || "0";

    if (individualTitle) {
      items.push({ title: individualTitle, price: individualPrice });
    }

    return items;
  }

  buildWorkshopItemsSection(items) {
    if (items.length === 0) return "";

    const itemsList = items
      .map(
        (item) =>
          `<li>${this.escapeHtml(item.title)}: ${this.escapeHtml(this.formatPrice(item.price))} ${this.escapeHtml(this.currencyValue)}</li>`
      )
      .join("");

    return `
        <p><strong>Workshops:</strong></p>
        <ul style="margin: 0 0 1em 0; padding-left: 20px;">
          ${itemsList}
        </ul>
    `;
  }

  formatPrice(value) {
    const num = parseFloat(value) || 0;
    return num.toLocaleString("de-DE", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  }
}
